import React, { useContext, useEffect, useRef, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import { getMyClientCompany, updateMyClientCompany } from "api/Clients";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import { Tab, Tabs } from "components/ui/Tabs";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import { CheckMarkIcon } from "components/svg";
import GeneralSettings from "./components/GeneralSettings";
import ApiSettings from "./components/ApiSettings";
import { Box, Spinner } from "theme-ui";
import { trackActivity } from "api/ActivityTracker";
import UserContext from "context/User";

const showApiTab = process.env.REACT_APP_ENVIRONMENT !== "sandbox";

export default function ClientCompany() {
  const toast = useToast();
  const [clientCompany, setClientCompany] = useState<any>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext<any>(UserContext);
  const lastTrackedTab = useRef<string | null>(null);
  const isClientAdmin = user?.client?.companyAdmin === true;

  // Non-admins should use /settings for personal preferences
  useEffect(() => {
    if (!isClientAdmin) {
      navigate("/settings", { replace: true });
    }
  }, [isClientAdmin, navigate]);

  const submitUpdate = async (values: any, actions: any) => {
    const previous = clientCompany;
    const changedFields: string[] = [];

    const isEqual = (a: any, b: any) => {
      if (a === b) return true;
      if (typeof a === "undefined" || a === null) {
        return typeof b === "undefined" || b === null;
      }
      if (typeof b === "undefined" || b === null) {
        return false;
      }
      try {
        return JSON.stringify(a) === JSON.stringify(b);
      } catch {
        return false;
      }
    };

    if (!isEqual(previous?.name, values?.name)) changedFields.push("name");
    if (!isEqual(previous?.phone, values?.phone)) changedFields.push("phone");
    if (!isEqual(previous?.contactEmail, values?.contactEmail)) changedFields.push("contactEmail");
    if (!isEqual(previous?.billingEmail, values?.billingEmail)) changedFields.push("billingEmail");
    if (!isEqual(previous?.address, values?.address)) changedFields.push("address");

    const update = {
      name: values.name,
      phone: values.phone,
      contactEmail: values.contactEmail,
      billingEmail: values.billingEmail,
      address: values.address,
    };

    const res = await updateMyClientCompany({ update });
    if (res?.id) {
      toast.add("Company updated.", "var(--theme-ui-colors-success)");
      await initClientCompany();

      if (changedFields.length > 0) {
        void trackActivity({
          module: "client_company_settings",
          action: "update_client_company_settings",
          actionType: "update",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "client",
          entityId: res?.id ?? null,
          metadata: { changedFields },
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
      }
    } else {
      toast.add(res?.message ? res.message : "Error updating company", "var(--theme-ui-colors-danger)");
    }
  };

  const UpdateSchema = Yup.object().shape({
    name: Yup.string().required("Company name is required."),
  });

  const initClientCompany = async () => {
    if (!isClientAdmin) return;
    const res = await getMyClientCompany();
    setClientCompany(res?.id ? res : null);
  };

  useEffect(() => {
    document.title = (process.env.REACT_APP_NAME || "JSCRM") + " | Company Settings";
    initClientCompany();
  }, []);

  useEffect(() => {
    if (!clientCompany?.id || !isClientAdmin) return;
    const params = new URLSearchParams(location.search);
    const activeTab = params.get("tab") || "general";
    if (lastTrackedTab.current === activeTab) return;
    lastTrackedTab.current = activeTab;
    void trackActivity({
      module: "client_company_settings",
      action: "view_client_company_settings",
      actionType: "view",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      entityType: "client",
      entityId: clientCompany.id,
      metadata: { tab: activeTab },
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
  }, [clientCompany?.id, location.search, user?.id, user?.company?.id]);

  const tabLinks = [
    { key: "general", label: "General" },
    ...(showApiTab ? [{ key: "api", label: "API" }] : []),
  ];

  if (!isClientAdmin) return null;

  return (
    <>
      <Header />
      <Page title="Company Settings" subtitle="Edit your organization settings.">
        {!clientCompany && (
          <Box sx={{ textAlign: "center" }}>
            <Spinner size={25} strokeWidth={3} title="loading" />
          </Box>
        )}
        {clientCompany && (
          <Formik
            initialValues={{ ...clientCompany }}
            onSubmit={submitUpdate}
            validationSchema={UpdateSchema}
            enableReinitialize
          >
            {({ isSubmitting, errors, values, submitCount, setFieldValue }) => (
              <Form noValidate autoComplete="off" style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                <Tabs
                  vertical
                  fillHeight
                  links={tabLinks}
                  stickyFooter
                  footer={
                    <BasicButton
                      styles={{ margin: ".25rem 0 .25rem 0" }}
                      type="submit"
                      $submitting={isSubmitting}
                      $iconHeight="14px"
                      $iconWidth="14px"
                      $loadingColor="#fff"
                      keepText={true}
                    >
                      {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                      Save Company
                    </BasicButton>
                  }
                >
                  <Tab itemKey="general">
                    <GeneralSettings
                      clientCompany={clientCompany}
                      errors={errors}
                      values={values}
                      submitCount={submitCount}
                      setFieldValue={setFieldValue}
                      initClientCompany={initClientCompany}
                    />
                  </Tab>
                  {showApiTab && (
                    <Tab itemKey="api">
                      <ApiSettings clientCompany={clientCompany} />
                    </Tab>
                  )}
                </Tabs>
              </Form>
            )}
          </Formik>
        )}
      </Page>
    </>
  );
}
